const GH_API = 'https://api.github.com';
const MAX_FILE_BYTES = 80_000;
const MAX_FILES = 12;
const MAX_TOTAL = 250_000;

const TEXT_EXT = /\.(md|txt|json|ya?ml|toml|js|jsx|ts|tsx|mjs|cjs|py|rb|go|rs|java|kt|swift|c|h|cpp|hpp|cs|php|sql|sh|css|scss|html|env\.example)$/i;
const PRIORITY = ['README.md', 'readme.md', 'README', 'package.json', 'pyproject.toml', 'go.mod', 'Cargo.toml', 'tsconfig.json'];

async function gh(path, token) {
  const r = await fetch(`${GH_API}${path}`, {
    headers: {
      Accept: 'application/vnd.github+json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!r.ok) throw new Error(`GitHub ${path} → ${r.status}`);
  return r.json();
}

export function parseRepoUrl(url) {
  // Accepts: https://github.com/owner/repo, git@github.com:owner/repo.git, owner/repo
  const trimmed = String(url || '').trim().replace(/\.git$/, '');
  const m =
    trimmed.match(/github\.com[/:]([^/]+)\/([^/?#]+)/) ||
    trimmed.match(/^([^/]+)\/([^/?#]+)$/);
  if (!m) return null;
  return { owner: m[1], repo: m[2] };
}

export async function listUserRepos(token) {
  const out = [];
  for (let page = 1; page <= 4; page++) {
    const items = await gh(`/user/repos?per_page=100&sort=updated&page=${page}`, token);
    out.push(...items);
    if (items.length < 100) break;
  }
  return out.map((r) => ({
    fullName: r.full_name,
    description: r.description,
    private: r.private,
    defaultBranch: r.default_branch,
    pushedAt: r.pushed_at,
  }));
}

export async function fetchRepoSnapshot({ owner, repo, token }) {
  const meta = await gh(`/repos/${owner}/${repo}`, token);
  const branch = meta.default_branch || 'main';
  const tree = await gh(`/repos/${owner}/${repo}/git/trees/${branch}?recursive=1`, token);
  const all = (tree.tree || []).filter((n) => n.type === 'blob');

  const fileList = all
    .map((n) => n.path)
    .filter((p) => !p.includes('node_modules/') && !p.startsWith('.next/') && !p.startsWith('dist/') && !p.startsWith('build/'))
    .slice(0, 400);

  const candidates = [
    ...PRIORITY.filter((p) => all.some((n) => n.path === p)),
    ...all.filter((n) => TEXT_EXT.test(n.path) && n.size && n.size < MAX_FILE_BYTES).map((n) => n.path),
  ];
  const seen = new Set();
  const ordered = candidates.filter((p) => (seen.has(p) ? false : (seen.add(p), true)));

  const files = [];
  let total = 0;
  for (const path of ordered) {
    if (files.length >= MAX_FILES || total >= MAX_TOTAL) break;
    try {
      const c = await gh(`/repos/${owner}/${repo}/contents/${encodeURIComponent(path).replace(/%2F/g, '/')}?ref=${branch}`, token);
      if (c.encoding !== 'base64' || !c.content) continue;
      const text = atob(c.content.replace(/\n/g, ''));
      const snippet = text.length > MAX_FILE_BYTES ? text.slice(0, MAX_FILE_BYTES) + '\n[...truncated]' : text;
      files.push({ path, snippet });
      total += snippet.length;
    } catch {
      // skip
    }
  }

  return {
    fullName: meta.full_name,
    description: meta.description || '',
    language: meta.language || '',
    defaultBranch: branch,
    fileList,
    files,
    fetchedAt: Date.now(),
  };
}

export function buildGithubPromptBlock(snapshot) {
  if (!snapshot) return '';
  const lines = [];
  lines.push(`GitHub repo: ${snapshot.fullName}${snapshot.language ? ` (${snapshot.language})` : ''}`);
  if (snapshot.description) lines.push(`Description: ${snapshot.description}`);
  if (snapshot.fileList?.length) {
    const sample = snapshot.fileList.slice(0, 80).join(', ');
    lines.push(`File tree (sample of ${snapshot.fileList.length}): ${sample}`);
  }
  if (snapshot.files?.length) {
    lines.push('Key files:');
    for (const f of snapshot.files) {
      lines.push(`--- ${f.path} ---\n${f.snippet}`);
    }
  }
  return lines.join('\n');
}
